/**
 * Conversas privadas dentro da mesa: 1:1 (system/pvtChat; FC.Room.UIModule.RoomPVTChat)
 * e em grupo (system/groupPVTChat; RoomGroupPVTChat), com a lista de quem está no
 * grupo, "convidar mais" e "sair da conversa". O log e a caixa de entrada são os do
 * chat principal (ChatModule com canal).
 */
import { useState } from 'react';
import { useT } from '../../i18n/index.js';
import type { User } from '../../shared.js';
import { useApp } from '../../store.js';
import { Button, CheckBox } from '../../ui/controls.js';
import { ChatModule } from './ChatModule.js';

export function PvtChatModule({ userId }: { userId: string }) {
  const t = useT();
  const present = useApp((s) => s.users.some((u) => u.id === userId));
  return (
    <div className="rd-pvt">
      {!present ? <div className="rd-pvt__notice">{t('pvt.away')}</div> : null}
      <ChatModule channel={{ kind: 'pvt', to: userId }} />
    </div>
  );
}

export function GroupChatModule({ groupId }: { groupId: string }) {
  const t = useT();
  const group = useApp((s) => s.groups[groupId]);
  const users = useApp((s) => s.users);
  const members = useApp((s) => s.members);
  const partGroup = useApp((s) => s.partGroup);
  const inviteToGroup = useApp((s) => s.inviteToGroup);
  const [inviting, setInviting] = useState(false);
  if (!group) return <div className="rd-module__placeholder">{t('pvt.group.gone')}</div>;
  const nameOf = (id: string) => members[id]?.nick ?? users.find((u) => u.id === id)?.displayName ?? t('pvt.group.away');
  const candidates = users.filter((u) => !group.members.includes(u.id));
  return (
    <div className="rd-pvt">
      <div className="rd-pvt__bar">
        <span className="rd-pvt__members" title={group.members.map(nameOf).join(', ')}>
          {group.members.map(nameOf).join(', ')}
        </span>
        <button type="button" className="rd-btn rd-btn--link" onClick={() => setInviting(true)}>
          {t('pvt.group.invite')}
        </button>
        <button type="button" className="rd-btn rd-btn--link" onClick={() => partGroup(groupId)}>
          {t('pvt.group.leave')}
        </button>
      </div>
      <ChatModule channel={{ kind: 'group', groupId }} />
      {inviting ? (
        <ChooseUsersDialog
          title={t('pvt.group.invite.title')}
          action={t('pvt.group.invite.action')}
          empty={t('pvt.group.invite.empty')}
          users={candidates}
          onClose={() => setInviting(false)}
          onChoose={(ids) => inviteToGroup(groupId, ids)}
        />
      ) : null}
    </div>
  );
}

/** Escolher pessoas da mesa (criar grupo / convidar mais). */
export function ChooseUsersDialog({
  title,
  action,
  empty,
  users,
  preselected = [],
  onClose,
  onChoose,
}: {
  title: string;
  action: string;
  empty: string;
  users: User[];
  preselected?: string[];
  onClose: () => void;
  onChoose: (userIds: string[]) => void;
}) {
  const t = useT();
  const members = useApp((s) => s.members);
  const [chosen, setChosen] = useState<string[]>(preselected.filter((id) => users.some((u) => u.id === id)));
  return (
    <div className="rd-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()} onKeyDown={(e) => e.key === 'Escape' && onClose()}>
      <form
        className="rd-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onSubmit={(e) => {
          e.preventDefault();
          if (chosen.length) {
            onChoose(chosen);
            onClose();
          }
        }}
      >
        <h2 className="rd-dialog__title">{title}</h2>
        <div className="rd-dialog__body rd-choose">
          {users.length === 0 ? <p>{empty}</p> : null}
          {users.map((u) => (
            <CheckBox key={u.id} checked={chosen.includes(u.id)} onChange={(on) => setChosen((c) => (on ? [...c, u.id] : c.filter((id) => id !== u.id)))}>
              {members[u.id]?.nick ? `${members[u.id]!.nick} (${u.displayName})` : u.displayName}
            </CheckBox>
          ))}
        </div>
        <div className="rd-dialog__actions">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('create.cancel')}
          </Button>
          <Button type="submit" variant="action" disabled={chosen.length === 0}>
            {action}
          </Button>
        </div>
      </form>
    </div>
  );
}

